import {Injectable} from "@angular/core";
import {Resource, ResourceType, ResourceUtils} from "../models/resource";
import {environment} from "@environments/environment";
import {RestApiManager} from "./rest-api-manager";
import {Metadata} from "../models/metadata";
import {Paginator} from "../models/paginator";
import {Equipment} from "../models/equipment";
import {MatDialog} from "@angular/material/dialog";
import {
  EditDialogData,
  EditDialogComponent
} from "../components/dialogs/edit-dialog/edit-dialog.component";
import {Utils} from "../utils";
import {AccountManagerService} from "./account-manager-service";
import {
  EditResourceDialogComponent,
  EditResourceDialogData
} from "../components/dialogs/edit-resource-dialog/edit-resource-dialog.component";

declare var $: any;

@Injectable({
  providedIn: 'root'
})
export class ResourceManager {

  constructor(
    private restApiManager: RestApiManager,
    private accountManager: AccountManagerService,
    private dialog: MatDialog,
  ) { }

  public createResource(resource: Resource, isDirty: boolean) {
    return this.restApiManager.post<Resource>(
      `${environment.restApi.endpoint}/resources`,
      { ...resource, isDirty },
      this.accountManager.account.authToken
    );
  }

  public getUploadSignedUrl(resource: Resource): Promise<{ bucket: string, objectName: string, url: string }> {
    return this.restApiManager.get<{ bucket: string, objectName: string, url: string }>(
      null,
      `${environment.restApi.endpoint}/resources/${resource.id}/file`,
      this.accountManager.account.authToken,
      { extension: resource.extension, content_type: resource.contentType }
    );
  }

  public getDownloadSignedUrl(resource: Resource): Promise<{ name: string, type: string, url: string}> {
    return this.restApiManager.get<{ name: string, type: string, url: string}>(
      null,
      `${environment.restApi.endpoint}/resources/${resource.id}`,
      this.accountManager.account.authToken
    );
  }

  public async uploadResource(model: Equipment, file: File, tags: string[], name?: string, hidden?: boolean): Promise<Resource> {

    // Get file extension
    const extension = Utils.getExtensionFromFile(file);

    // Create new resource
    let resource = new Resource();
    resource.modelId = model.id;
    resource.name = name || file.name;
    resource.typeId = ResourceUtils.ExtensionToResourceType(extension);
    resource.tags = tags;
    resource = await this.createResource(resource, hidden);

    // Get upload signed url
    resource.contentType = ResourceUtils.FileToContentType(file);
    resource.extension = extension;
    const {bucket, objectName, url} = await this.getUploadSignedUrl(resource);

    // Upload file
    await this.uploadResourceFile(url, file, resource.contentType);

    // Set file upload as completed
    await this.setFile(resource.id, bucket, objectName, resource.contentType);

    return resource;

  }

  public async uploadResourceFile(url: string, file: File, contentType: string) {
    return new Promise((resolve, reject) => {
      $.ajax({
        type: 'PUT',
        url: url,
        data: file,
        contentType,
        processData: false,
        success: (data: any, textStatus: string, jqXHR: XMLHttpRequest) => {
          resolve();
        }
      });
    });
  }

  public async setFile(resourceId: string, bucket: string, objectName: string, contentType: string): Promise<void> {
    await this.restApiManager.patch<Object>(
      `${environment.restApi.endpoint}/resources/${resourceId}/file`,
      {bucket, objectName, contentType},
      this.accountManager.account.authToken
    );
  }

  public async setResourceMetadata(resource: Resource, metadata: Metadata): Promise<void> {
    await Promise.all(Object.keys(metadata).map(key => {
      this.restApiManager.post<Resource>(
        `${environment.restApi.endpoint}/resource_metadata`,
        { resourceId: resource.id, name: key, value: metadata[key] },
        this.accountManager.account.authToken
      )
    }));
  }

  public async getResourceMetadata(resource: Resource, paginator?: Paginator<Metadata>): Promise<Metadata> {
    const metadata = await this.restApiManager.getAll<{ name: string, value: string }>(
      null,
      `${environment.restApi.endpoint}/resources/${resource.id}/metadata`,
      this.accountManager.account.authToken,
      { paginator }
    );

    const metaObj = new Metadata();

    metadata.forEach(meta => {
      switch (meta.name) {
        case 'duration':
          metaObj.duration = parseFloat(meta.value);
          break;
        case 'size':
          metaObj.size = parseFloat(meta.value);
          break;
        case 'contentType':
          metaObj.contentType = meta.value;
          break;
        case 'recStart':
          metaObj.recStart = parseInt(meta.value);
          break;
        case 'recEnd':
          metaObj.recEnd = parseInt(meta.value);
          break;
      }
    });

    return metaObj;
  }

  async getResources(equipmentId: string, paginator?: Paginator<Resource>): Promise<Resource[]> {
    return this.restApiManager.getAll<Resource>(
      Resource,
      `${environment.restApi.endpoint}/equipment_models/${equipmentId}/resources`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  async getStepResources(stepId: string, paginator?: Paginator<Resource>): Promise<Resource[]> {
    return this.restApiManager.getAll<Resource>(
      Resource,
      `${environment.restApi.endpoint}/steps/${stepId}/resources`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  addStepResources(stepId: string, resourceIds: string[]) {
    return this.restApiManager.post<Resource>(
      `${environment.restApi.endpoint}/steps/${stepId}/resources`,
      { resourceId: resourceIds },
      this.accountManager.account.authToken
    );
  }

  removeStepResources(stepId: string, resourceIds: string[]) {
    return Promise.all(
      resourceIds.map(resourceId => this.restApiManager.delete(
        `${environment.restApi.endpoint}/steps/${stepId}/resources/${resourceId}`,
        this.accountManager.account.authToken
      ))
    );
  }

  editResource(resource: Resource, uploadableResources: ResourceType[]) {

    const dialogRef = this.dialog.open(EditResourceDialogComponent, {
      width: '350px',
      data: {
        isNew: false,
        name: resource.name,
        tags: resource.tags,
        uploadableResources
      } as EditResourceDialogData,
    });

    dialogRef.afterClosed().subscribe(async (value: EditResourceDialogData) => {
      if (value) {
        resource.name = value.name;
        resource.tags = value.tags;
        await this.restApiManager.put(
          `${environment.restApi.endpoint}/resources/${resource.id}`,
          { name: resource.name, modelId: resource.equipmentModel.id, typeId: resource.type, tags: resource.tags },
          this.accountManager.account.authToken
        );
      }
    });

  }

  public async removeResource(resource: Resource): Promise<void> {

    // Remove resource
    await this.restApiManager.delete(
      `${environment.restApi.endpoint}/resources/${resource.id}`,
      this.accountManager.account.authToken
    );

  }

}
